/**
 * Filter persistence utility with hybrid storage approach and business-unit scoping
 * - Universal filters (divisions, locations) → localStorage per business unit
 * - Page-specific filters → sessionStorage per business unit
 */

import { getActivityKey, STORAGE_KEY_PREFIXES } from 'src/contstant/storageKeys';

const TTL_HOURS = 4;

/**
 * Check if stored data has expired for a specific business unit
 */
export function isExpired(businessUnitId: string): boolean {
  const activityKey = getActivityKey(businessUnitId);
  const lastActivity = localStorage.getItem(activityKey);
  if (!lastActivity) return true;

  const lastActivityTime = parseInt(lastActivity, 10);
  const now = Date.now();
  const hoursSinceActivity = (now - lastActivityTime) / (1000 * 60 * 60);

  return hoursSinceActivity > TTL_HOURS;
}

/**
 * Update activity timestamp for a specific business unit
 */
export function updateActivity(businessUnitId: string): void {
  const activityKey = getActivityKey(businessUnitId);
  localStorage.setItem(activityKey, Date.now().toString());
}

/**
 * Load filters from sessionStorage (page-specific) for a specific business unit
 */
export function loadFilters<T>(key: string, defaultValue: T, businessUnitId: string): T {
  try {
    if (isExpired(businessUnitId)) {
      // Clear expired data for this business unit
      clearFiltersForBusinessUnit(businessUnitId);
      return defaultValue;
    }

    const stored = sessionStorage.getItem(key);
    if (stored) {
      return JSON.parse(stored);
    }
  } catch (error) {
    console.error('Failed to load filters:', error);
  }

  return defaultValue;
}

/**
 * Save filters to sessionStorage (page-specific) for a specific business unit
 */
export function saveFilters<T>(key: string, value: T, businessUnitId: string): void {
  try {
    sessionStorage.setItem(key, JSON.stringify(value));
    updateActivity(businessUnitId);
  } catch (error) {
    console.error('Failed to save filters:', error);
  }
}

/**
 * Clear all filter data for a specific business unit from both storages
 */
export function clearFiltersForBusinessUnit(businessUnitId: string): void {
  try {
    // Clear sessionStorage (page-specific filters + universal session copies for this BU)
    const sessionKeys = Object.keys(sessionStorage);
    sessionKeys.forEach((key) => {
      if (
        key.startsWith(`${STORAGE_KEY_PREFIXES.FILTERS}${businessUnitId}:`) ||
        key.startsWith(`${STORAGE_KEY_PREFIXES.SESSION_UNIVERSAL}${businessUnitId}:`)
      ) {
        sessionStorage.removeItem(key);
      }
    });

    // Clear localStorage (universal filters + metadata for this BU)
    const localKeys = Object.keys(localStorage);
    localKeys.forEach((key) => {
      if (
        key.startsWith(`${STORAGE_KEY_PREFIXES.UNIVERSAL}${businessUnitId}:`) ||
        key === getActivityKey(businessUnitId)
      ) {
        localStorage.removeItem(key);
      }
    });
  } catch (error) {
    console.error('Failed to clear filters for business unit:', error);
  }
}

/**
 * Clear all filter data from both storages (all business units)
 * Use with caution - this clears filters for ALL business units
 */
export function clearAllFilters(): void {
  try {
    // Clear sessionStorage (page-specific filters + universal session copies)
    const sessionKeys = Object.keys(sessionStorage);
    sessionKeys.forEach((key) => {
      if (
        key.startsWith(STORAGE_KEY_PREFIXES.FILTERS) ||
        key.startsWith(STORAGE_KEY_PREFIXES.SESSION_UNIVERSAL)
      ) {
        sessionStorage.removeItem(key);
      }
    });

    // Clear localStorage (universal filters + metadata)
    const localKeys = Object.keys(localStorage);
    localKeys.forEach((key) => {
      if (
        key.startsWith(STORAGE_KEY_PREFIXES.FILTER) ||
        key.startsWith(STORAGE_KEY_PREFIXES.UNIVERSAL)
      ) {
        localStorage.removeItem(key);
      }
    });
  } catch (error) {
    console.error('Failed to clear all filters:', error);
  }
}

/**
 * Count active filters in a filter object
 * Excludes specified fields and empty values
 *
 * @param filters - The filter object to count
 * @param excludeFields - Array of field names to exclude from counting
 * @returns Number of active filters
 */
export function countActiveFilters<T extends Record<string, any>>(
  filters: T | undefined,
  excludeFields: string[] = [],
): number {
  if (!filters) return 0;

  let count = 0;

  Object.entries(filters).forEach(([key, value]) => {
    // Skip excluded fields
    if (excludeFields.includes(key)) return;

    // Count non-empty values
    if (value !== undefined && value !== null && value !== '') {
      // Handle arrays
      if (Array.isArray(value)) {
        if (value.length > 0) count++;
      }
      // Handle objects (like dateRange)
      else if (typeof value === 'object') {
        // Count if object has any non-empty properties
        const hasValue = Object.values(value).some(
          (v) => v !== undefined && v !== null && v !== '',
        );
        if (hasValue) count++;
      }
      // Handle primitives
      else {
        count++;
      }
    }
  });

  return count;
}
